/* ============================================================================
 * Zoe Product Section
 *  - Updates "Selected Size" + "Selected color" summary on variant change
 *  - Cart-aware free shipping nudge (re-checks /cart.js on page load)
 *
 *  Does NOT move any DOM around. The summary, size guide, and variant picker
 *  all stay where Liquid put them — Dawn re-renders parts of the section on
 *  variant change, and moving things into Dawn's elements gets them wiped.
 * ========================================================================== */

(function () {
  'use strict';

  // ----- Suppress the image flash on size-only variant changes ---------------
  // Dawn's product-info.js calls updateMedia() on every variant change. For a
  // size change the featured_media is the same as before, so setActiveMedia
  // ends up removing then re-adding `.is-active` on the SAME slide. CSS
  // transitions render that no-op toggle as a visible blink. We override
  // updateMedia to bail early when the target image is already the active
  // one — that path stays fully intact for genuine colour changes.
  //
  // Override lives here (not in product-info.js) so theme updates don't wipe
  // it. zoe-product.js is loaded AFTER product-info.js (both deferred, in
  // document order) so the custom element is already registered.
  const ProductInfoClass = customElements.get('product-info');
  if (ProductInfoClass && ProductInfoClass.prototype.updateMedia) {
    const originalUpdateMedia = ProductInfoClass.prototype.updateMedia;
    ProductInfoClass.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      if (variantFeaturedMediaId) {
        const targetId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const gallery = this.querySelector('media-gallery');
        const activeSlide = gallery && gallery.querySelector('li.is-active[data-media-id]');
        if (activeSlide && activeSlide.dataset.mediaId === targetId) {
          // Same image already showing — nothing to update, skip the churn.
          return;
        }
      }
      return originalUpdateMedia.call(this, html, variantFeaturedMediaId);
    };
  }
  // ---------------------------------------------------------------------------

  // Module-scoped so re-init (or section reload in the theme editor) can
  // tear down the previous observer before creating a new one.
  let sizeGuideObserver = null;

  function init() {
    const productInfo = document.querySelector('product-info[data-section]');
    if (!productInfo) return;

    const sectionId = productInfo.dataset.section;
    const selectionBox = document.querySelector('.zoe-selection-box');

    // Size Guide placement:
    //   Desktop (>=750px): inline with the size pills, right-aligned.
    //   Mobile  (<750px):  on its own line below the size pills, left-aligned.
    // The JS reference keeps the element alive across Dawn's variant-selects
    // re-renders; a MutationObserver re-runs placement when the DOM changes,
    // and a resize listener re-runs it when crossing the breakpoint.
    let sizeGuideRef = document.querySelector('.zoe-size-guide-opener');
    const mqDesktop = window.matchMedia('(min-width: 750px)');
    function placeSizeGuide() {
      if (!sizeGuideRef) return;
      const row = document.querySelector('.zoe-selection-box .faffle-size-buttons');
      if (!row) return;
      if (mqDesktop.matches) {
        // Inline: append as last child of size buttons row.
        if (sizeGuideRef.parentElement !== row) {
          row.appendChild(sizeGuideRef);
        }
      } else {
        // Stacked: place as sibling immediately after size buttons row.
        if (sizeGuideRef.previousElementSibling !== row) {
          row.insertAdjacentElement('afterend', sizeGuideRef);
        }
      }
    }
    placeSizeGuide();
    if (selectionBox && sizeGuideRef) {
      if (sizeGuideObserver) sizeGuideObserver.disconnect();
      sizeGuideObserver = new MutationObserver(function () { placeSizeGuide(); });
      sizeGuideObserver.observe(selectionBox, { childList: true, subtree: true });
    }
    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener('change', placeSizeGuide);
    } else {
      window.addEventListener('resize', placeSizeGuide);
    }

    // Colour hex map (rendered as a JSON blob in the section).
    let colorHexMap = {};
    try {
      const blob = document.getElementById('ZoeColorHexMap-' + sectionId);
      if (blob) colorHexMap = JSON.parse(blob.textContent) || {};
    } catch (_) {}

    // Lock scroll position when SIZE changes (not colour — colour swaps the
     // product image so a refresh is fine). Same pattern as sticky-add-to-cart:
     // override scroll APIs for ~1s so Dawn's post-fetch focus/auto-scroll
     // can't jump the user back to the top. Capture phase = runs BEFORE
     // Dawn's bubble-phase change handler.
    document.addEventListener('change', function (e) {
      const target = e.target;
      if (!target || !target.classList || !target.classList.contains('product-variant-option-input')) return;
      const optionName = (target.dataset.optionName || '').toLowerCase();
      if (optionName.indexOf('size') === -1) return;

      const scrollPos = window.scrollY;
      const origScrollTo = window.scrollTo;
      const origScroll = window.scroll;
      const origScrollIntoView = Element.prototype.scrollIntoView;
      window.scrollTo = function () { origScrollTo.call(window, 0, scrollPos); };
      window.scroll = function () { origScroll.call(window, 0, scrollPos); };
      Element.prototype.scrollIntoView = function () {};

      let ticks = 0;
      const interval = setInterval(function () {
        origScrollTo.call(window, 0, scrollPos);
        ticks++;
        if (ticks > 20) {
          clearInterval(interval);
          window.scrollTo = origScrollTo;
          window.scroll = origScroll;
          Element.prototype.scrollIntoView = origScrollIntoView;
        }
      }, 50);
    }, true);

    // Preload the rest of the gallery images when the customer first picks
    // a colour swatch. Page load stays light (only the hero loads eagerly),
    // but the moment a customer signals interest by changing colour, the
    // remaining shots start downloading in the background — so thumbnail
    // clicks are instant rather than waiting on a fresh network round-trip.
    //
    // One-shot: once kicked off, never run again for this page view. Re-running
    // would be a no-op (browser dedupes via HTTP cache) but it's cleaner to skip.
    let galleryPreloaded = false;
    function preloadGalleryImages() {
      if (galleryPreloaded) return;
      galleryPreloaded = true;
      const lazyImgs = document.querySelectorAll('.product__media-item img[loading="lazy"]');
      lazyImgs.forEach(function (img) {
        img.loading = 'eager';
        // Belt-and-braces: also kick off a parallel fetch via a detached Image
        // element. Browsers vary on whether flipping `loading` alone triggers
        // an immediate load on an already-deferred img, but they all honour
        // a fresh Image() request, and the HTTP cache will share the result.
        const preloader = new Image();
        if (img.srcset) preloader.srcset = img.srcset;
        if (img.sizes) preloader.sizes = img.sizes;
        if (img.src) preloader.src = img.src;
      });
    }

    // Live update of the summary on variant change.
    // Faffle's custom variant picker uses inputs with class
    // `.product-variant-option-input` and `data-option-name` (not Dawn's
    // standard `options[Color]` names). Listen on the document so the handler
    // survives any DOM swap Dawn does on variant change.
    document.addEventListener('change', function (e) {
      const target = e.target;
      if (!target || !target.classList || !target.classList.contains('product-variant-option-input')) return;
      const optionName = (target.dataset.optionName || '').toLowerCase();
      const newValue = target.value;

      if (optionName.indexOf('size') !== -1) {
        const sizeEl = document.querySelector('[data-zoe-summary-size]');
        if (sizeEl) sizeEl.textContent = newValue;
      }
      if (optionName.indexOf('color') !== -1 || optionName.indexOf('colour') !== -1) {
        preloadGalleryImages();

        // Mobile-only: briefly fade the gallery to mask the display:none
        // reshuffle that happens as Shopify swaps the visible colour's slides.
        // Desktop already crossfades via the grid-stacking CSS, so we skip it
        // there to avoid double-fading.
        if (window.matchMedia('(max-width: 749px)').matches) {
          const mediaList = document.querySelector('.product__media-list');
          if (mediaList) {
            mediaList.classList.add('faffle-swatch-fading');
            // 450ms covers the variant fetch (~200-300ms) + applyVariantMediaFilter's
            // 150ms delayed pass. By then the new colour's slides are in place.
            setTimeout(function () {
              mediaList.classList.remove('faffle-swatch-fading');
            }, 450);
          }
        }

        const swatchEl = document.querySelector('[data-zoe-summary-color-swatch]');
        if (swatchEl) {
          let hex = colorHexMap[newValue] || newValue.toLowerCase().replace(/\s+/g, '');
          if (hex.charAt(0) !== '#') hex = '#' + hex;
          swatchEl.style.backgroundColor = hex;
          swatchEl.setAttribute('title', newValue);
        }
      }
    });

    // Gallery prev/next arrow overlays — adds visible navigation on the main
    // product image (desktop). On mobile Dawn's built-in slider arrows already
    // appear, so CSS hides ours there. Clicking advances/retreats one thumbnail.
    function setupGalleryNav() {
      const mediaGallery = document.querySelector('.product__media-wrapper media-gallery');
      if (!mediaGallery) return;
      const mainSlider = mediaGallery.querySelector('slider-component:not(.thumbnail-slider)');
      if (!mainSlider) return;
      if (mainSlider.querySelector('.zoe-gallery-nav')) return;

      function step(direction) {
        // Only cycle through VISIBLE thumbnails. On desktop Dawn filters the
        // strip by the selected colour variant, so hidden items shouldn't be
        // part of the navigation. `offsetParent === null` catches display:none.
        const thumbs = Array.from(document.querySelectorAll('.thumbnail-list__item'))
          .filter(function (item) { return item.offsetParent !== null; })
          .map(function (item) { return item.querySelector('.thumbnail'); })
          .filter(Boolean);
        if (!thumbs.length) return;
        let idx = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
        if (idx < 0) idx = 0;
        let next = idx + direction;
        if (next < 0) next = thumbs.length - 1;
        if (next >= thumbs.length) next = 0;
        thumbs[next].click();
      }

      const group = document.createElement('div');
      group.className = 'zoe-gallery-nav-group';

      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'zoe-gallery-nav zoe-gallery-nav--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '<svg viewBox="0 0 10 16" aria-hidden="true"><path d="M8 1L1 8l7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      prev.addEventListener('click', function (e) { e.preventDefault(); step(-1); });

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'zoe-gallery-nav zoe-gallery-nav--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '<svg viewBox="0 0 10 16" aria-hidden="true"><path d="M2 1l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      next.addEventListener('click', function (e) { e.preventDefault(); step(1); });

      group.appendChild(prev);
      group.appendChild(next);

      if (getComputedStyle(mainSlider).position === 'static') {
        mainSlider.style.position = 'relative';
      }
      mainSlider.appendChild(group);

      // Keyboard arrows for power users.
      document.addEventListener('keydown', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable)) return;
        if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      });
    }
    setupGalleryNav();

    // Cart-aware free shipping nudge. Server renders an initial message based
    // on the user's IP-detected country + cart at request time; we refresh
    // from /cart.js on load so the nudge reflects the latest cart state.
    const nudge = document.querySelector('[data-zoe-shipping-nudge]');
    if (nudge) {
      const threshold = parseInt(nudge.dataset.threshold, 10);
      const cartUrl = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js';
      fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || typeof cart.total_price !== 'number') return;
          let state;
          let msg;
          if (cart.total_price <= 0) {
            state = 'empty';
            msg = nudge.dataset.msgEmpty;
          } else if (cart.total_price >= threshold) {
            state = 'unlocked';
            msg = nudge.dataset.msgUnlocked;
          } else {
            state = 'below';
            msg = nudge.dataset.msgBelow;
          }
          if (state !== nudge.dataset.state) {
            nudge.dataset.state = state;
            nudge.innerHTML = msg;
          }
        })
        .catch(function () { /* server-rendered fallback already shown */ });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Tear down the observer when Shopify removes the section in the theme
  // editor, so the reference can't outlive its detached DOM target.
  // We deliberately do NOT re-run init() on section:load — the document-level
  // change/keydown listeners would stack. Re-running init is a bigger refactor.
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector('product-info[data-section]')) {
      if (sizeGuideObserver) {
        sizeGuideObserver.disconnect();
        sizeGuideObserver = null;
      }
    }
  });
})();
